// src/components/admin/shared/NotificationBar.js
import React, { useState, useEffect } from 'react';
import { CheckCircle, AlertCircle, X } from 'lucide-react';

const NotificationBar = ({ type, message, onClose, duration = 5000 }) => {
  const [isVisible, setIsVisible] = useState(true);
  
  useEffect(() => {
    if (duration) {
      const timer = setTimeout(() => {
        setIsVisible(false);
        if (onClose) setTimeout(onClose, 300); // Allow animation to complete
      }, duration);
      
      return () => clearTimeout(timer);
    }
  }, [duration, onClose]);
  
  const handleClose = () => {
    setIsVisible(false);
    if (onClose) setTimeout(onClose, 300); // Allow animation to complete
  };
  
  const getTypeStyles = () => {
    switch (type) {
      case 'success':
        return {
          bg: 'bg-green-50',
          border: 'border-green-400',
          text: 'text-green-800',
          icon: <CheckCircle className="h-5 w-5 text-green-400" />
        };
      case 'error':
        return {
          bg: 'bg-red-50',
          border: 'border-red-400',
          text: 'text-red-800',
          icon: <AlertCircle className="h-5 w-5 text-red-400" />
        };
      case 'warning':
        return {
          bg: 'bg-amber-50',
          border: 'border-amber-400',
          text: 'text-amber-800',
          icon: <AlertCircle className="h-5 w-5 text-amber-400" />
        };
      case 'info':
      default:
        return {
          bg: 'bg-blue-50',
          border: 'border-blue-400',
          text: 'text-blue-800',
          icon: <CheckCircle className="h-5 w-5 text-blue-400" />
        };
    }
  };
  
  const styles = getTypeStyles();
  
  return (
    <div 
      className={`fixed top-4 right-4 z-50 transition-transform transform ${
        isVisible ? 'translate-x-0' : 'translate-x-full'
      } duration-300 ease-in-out max-w-md w-full`}
    >
      <div className={`rounded-md p-4 ${styles.bg} border ${styles.border}`}>
        <div className="flex">
          <div className="flex-shrink-0">
            {styles.icon}
          </div>
          <div className="ml-3">
            <p className={`text-sm font-medium ${styles.text}`}>{message}</p>
          </div>
          <div className="ml-auto pl-3">
            <div className="-mx-1.5 -my-1.5">
              <button
                onClick={handleClose}
                className={`inline-flex rounded-md p-1.5 ${styles.text} hover:bg-white focus:outline-none`}
              >
                <span className="sr-only">Dismiss</span>
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationBar;